import { t, te } from '@/i18n/t'
import { isAgreementDeclined } from '@/utils/agreementGatePrompt'
import { ApiError } from '@/utils/apiClient'

// errorMessage — turn any thrown error into a localized, user-facing string.
//
// Precedence:
//   1. A backend stable code (body.code) with a localized message → errors.<code>
//   2. A generic message for the HTTP status (400/401/403/404/409/…)
//   3. The raw backend `error` text (backward-compatible for un-coded 4xx)
//   4. A generic fallback (network / non-ApiError / 5xx)
//
// Replaces the old "show e.message or a hardcoded fallback" pattern so the UI
// never shows a raw, mixed-language backend string and a language switch
// actually translates errors.
const BY_STATUS: Record<number, string> = {
  400: 'errors.badRequest',
  401: 'errors.unauthorized',
  403: 'errors.forbidden',
  404: 'errors.notFound',
  409: 'errors.conflict',
  // 410 is the product's expired-magic-link status (booking, invitee,
  // betalingslenke, signup verify). Without an entry here an un-coded
  // 410 fell through to arm 3 and rendered the raw backend string —
  // fine while every 410 happened to be Norwegian, a leak the moment
  // one isn't. errors.gone is worded as the twin of the backend's
  // errMagicTokenExpired, so no existing surface loses wording.
  410: 'errors.gone',
  413: 'errors.tooLarge',
  422: 'errors.validation',
  429: 'errors.rateLimited',
}

export function errorMessage(e: unknown, fallbackKey = 'errors.generic'): string {
  // The operator backed out of the agreement-gate confirmation (#3287).
  // Nothing failed — they declined — so there is nothing to report. Callers
  // store this behind a v-if, so an empty string renders no error at all.
  if (isAgreementDeclined(e)) return ''
  if (e instanceof ApiError) {
    const code = typeof e.body.code === 'string' ? e.body.code : ''
    if (code && te(`errors.${code}`)) return t(`errors.${code}`)

    const statusKey = BY_STATUS[e.status]
    if (statusKey) return t(statusKey)
    if (e.status >= 500) return t('errors.server')

    // Un-coded 4xx we don't have a generic for: keep the backend text.
    return e.message || t(fallbackKey)
  }
  return t(fallbackKey)
}
